import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import StaffForm, { Staff } from '../components/StaffForm';
import AccessModal from '../components/AccessModal';
import TableFilter from '../components/TableFilter';
import ActionButtons from '../components/ActionButtons';
import ReportModal from '../components/ReportModal';

// Atualizando a interface para refletir a estrutura real do usuário aninhado
interface StaffWithUser extends Staff {
  user?: { id: number; username: string; is_active: boolean };
}

const StaffPage: React.FC = () => {
  const [staffList, setStaffList] = useState<StaffWithUser[]>([]);
  const [filteredStaff, setFilteredStaff] = useState<StaffWithUser[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingStaff, setEditingStaff] = useState<StaffWithUser | null>(null);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [selectedStaffForAccess, setSelectedStaffForAccess] = useState<StaffWithUser | null>(null);

  // Modal de Relatórios
  const [reportModal, setReportModal] = useState<{ isOpen: boolean; rowId?: number; isRowReport: boolean }>({
    isOpen: false,
    isRowReport: false
  });

  const fetchStaff = useCallback(async () => {
    try {
      const response = await api.get('staff/');
      setStaffList(response.data);
      setFilteredStaff(response.data);
    } catch (error) {
      console.error("Error fetching staff", error);
    }
  }, []);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  const handleFilter = (filters: { [key: string]: string }) => {
    let result = staffList;

    if (filters.name) {
      result = result.filter(s => s.name.toLowerCase().includes(filters.name.toLowerCase()));
    }
    if (filters.email) {
      result = result.filter(s => s.email.toLowerCase().includes(filters.email.toLowerCase()));
    }
    if (filters.role) {
      result = result.filter(s => s.role.toLowerCase().includes(filters.role.toLowerCase()));
    }

    setFilteredStaff(result);
  };

  const handleSuccess = () => {
    setShowForm(false);
    setEditingStaff(null);
    fetchStaff();
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingStaff(null);
  };

  const handleEdit = (staff: StaffWithUser) => {
    setEditingStaff(staff);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir este colaborador?')) {
      try {
        await api.delete(`staff/${id}/`);
        fetchStaff();
      } catch (error) {
        console.error("Error deleting staff", error);
        alert('Erro ao excluir colaborador.');
      }
    }
  };

  const handleGenerateAccess = (staff: StaffWithUser) => {
    setSelectedStaffForAccess(staff);
    setShowAccessModal(true);
  };

  const handleAccessSuccess = () => {
    setShowAccessModal(false);
    setSelectedStaffForAccess(null);
    fetchStaff();
  };

  const handleAccessCancel = () => {
    setShowAccessModal(false);
    setSelectedStaffForAccess(null);
  };

  const openGeneralReports = () => setReportModal({ isOpen: true, isRowReport: false });
  const openRowReport = (id: number) => setReportModal({ isOpen: true, rowId: id, isRowReport: true });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1>Colaboradores</h1>
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Gerencie a equipe e seus acessos</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-secondary" onClick={openGeneralReports}>Relatórios da Equipe</button>
          {!showForm && (
            <button className="btn" onClick={() => setShowForm(true)}>
              + Novo Colaborador
            </button>
          )}
        </div>
      </div>

      {showForm ? (
        <div style={{ marginBottom: '30px' }}>
          <StaffForm
            onSuccess={handleSuccess}
            onCancel={handleCancel}
            staffToEdit={editingStaff}
          />
        </div>
      ) : (
        <TableFilter
          filters={[
            { key: 'name', label: 'Nome' },
            { key: 'email', label: 'Email' },
            { key: 'role', label: 'Função' }
          ]}
          onFilter={handleFilter}
        />
      )}

      {showAccessModal && selectedStaffForAccess && (
        <AccessModal
          staff={selectedStaffForAccess}
          onSuccess={handleAccessSuccess}
          onCancel={handleAccessCancel}
        />
      )}

      {reportModal.isOpen && (
        <ReportModal
          isOpen={reportModal.isOpen}
          onClose={() => setReportModal({ ...reportModal, isOpen: false })}
          category="Staff"
          rowId={reportModal.rowId}
          isRowReport={reportModal.isRowReport}
        />
      )}

      <div className="card" style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th>Nome</th>
              <th>Email</th>
              <th>Função</th>
              <th>Status Cadastro</th>
              <th>Acesso ao Sistema</th>
              <th style={{ textAlign: 'right' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredStaff.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: '30px', color: 'var(--text-light)' }}>
                  Nenhum colaborador encontrado.
                </td>
              </tr>
            ) : (
              filteredStaff.map((staff) => (
                <tr key={staff.id}>
                  <td style={{ fontWeight: '500' }}>{staff.name}</td>
                  <td>{staff.email}</td>
                  <td>{staff.role}</td>
                  <td>
                    {staff.is_active ? (
                      <span className="badge badge-success">Ativo</span>
                    ) : (
                      <span className="badge badge-danger">Inativo</span>
                    )}
                  </td>
                  <td>
                    {staff.user ? (
                      staff.user.is_active ? (
                        <span className="badge badge-success">Ativo</span>
                      ) : (
                        <span className="badge badge-danger">Inativo</span>
                      )
                    ) : (
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '4px 8px', fontSize: '0.75rem', background: 'var(--primary-light)', color: 'var(--primary-blue)', border: 'none' }}
                        onClick={() => handleGenerateAccess(staff)}
                      >
                        Gerar Acesso
                      </button>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <ActionButtons
                      onEdit={() => handleEdit(staff)}
                      onDelete={() => staff.id && handleDelete(staff.id)}
                      onReport={() => staff.id && openRowReport(staff.id)}
                      deletePermission="delete_staff"
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StaffPage;
